import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Users, ListChecks, DollarSign, BadgeCheck, Wallet, CreditCard, Route, RefreshCw, AlertTriangle } from "lucide-react";
import RunSheet from "@/components/RunSheet";

const STATUSES = ["pending", "scheduled", "in_progress", "completed", "cancelled"];
const STATUS_CLS = {
  pending: "bg-status-pending-bg text-status-pending-text",
  scheduled: "bg-status-scheduled-bg text-status-scheduled-text",
  in_progress: "bg-status-progress-bg text-status-progress-text",
  completed: "bg-deep-forest text-warm-sand",
  cancelled: "bg-moss text-deep-forest/60",
};

export default function AdminPanel() {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(true);
  const [refundingId, setRefundingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [b, u, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/users"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setUsers(u.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, status } : b));
      toast.success(`Status: ${status}`);
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/mark-paid`);
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, payment_status: "paid" } : b));
      toast.success("Marked as paid");
    } catch (e) {
      toast.error("Could not mark paid");
    }
  };

  const retryRefund = async (id) => {
    setRefundingId(id);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/refund`);
      toast.success(`Refund ${data.refund_status || "issued"}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setRefundingId(null);
    }
  };

  const markRefunded = async (id) => {
    setRefundingId(id);
    try {
      await api.post(`/admin/bookings/${id}/mark-refunded`);
      toast.success("Marked as refunded");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark refunded");
    } finally {
      setRefundingId(null);
    }
  };

  const refundQueue = useMemo(() => bookings.filter((b) => b.needs_refund), [bookings]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14" data-testid="admin-panel">
      <div className="label-overline mb-2">— Admin</div>
      <h1 className="font-serif text-4xl lg:text-5xl text-deep-forest tracking-tight">Operations.</h1>

      {/* stats */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-deep-forest/10 border border-deep-forest/10">
        <AStat icon={Users} label="Customers" value={stats?.total_users ?? "—"} testId="admin-stat-users" />
        <AStat icon={ListChecks} label="Bookings" value={stats?.total_bookings ?? "—"} testId="admin-stat-bookings" />
        <AStat icon={BadgeCheck} label="Paid" value={stats?.paid_bookings ?? "—"} testId="admin-stat-paid" />
        <AStat
          icon={DollarSign}
          label="Net revenue"
          value={stats ? `$${stats.total_revenue.toFixed(2)}` : "—"}
          testId="admin-stat-revenue"
          sub={stats && stats.refunds_count > 0 ? `− $${stats.refunds_total.toFixed(2)} refunded (${stats.refunds_count})` : null}
        />
      </div>

      <div className="mt-12 flex gap-8 border-b border-deep-forest/10">
        <TabBtn id="bookings" tab={tab} setTab={setTab} label="Bookings" testId="admin-tab-bookings" />
        <TabBtn id="run-sheet" tab={tab} setTab={setTab} label="Run sheet" testId="admin-tab-run-sheet" />
        <TabBtn
          id="refunds"
          tab={tab}
          setTab={setTab}
          label={refundQueue.length > 0 ? `Needs refund (${refundQueue.length})` : "Needs refund"}
          testId="admin-tab-refunds"
          highlight={refundQueue.length > 0}
        />
        <TabBtn id="users" tab={tab} setTab={setTab} label="Customers" testId="admin-tab-users" />
      </div>

      {loading ? (
        <div className="text-sm text-deep-forest/60 mt-10" data-testid="admin-loading">Loading…</div>
      ) : tab === "run-sheet" ? (
        <RunSheet />
      ) : tab === "refunds" ? (
        <RefundsTable
          rows={refundQueue}
          onRetryRefund={retryRefund}
          onMarkRefunded={markRefunded}
          refundingId={refundingId}
        />
      ) : tab === "bookings" ? (
        <div className="mt-8 overflow-x-auto" data-testid="admin-bookings-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-deep-forest/60 border-b border-deep-forest/10">
                <th className="py-4">Customer</th>
                <th className="py-4">Areas</th>
                <th className="py-4">Date</th>
                <th className="py-4">Address</th>
                <th className="py-4">Price</th>
                <th className="py-4">Payment</th>
                <th className="py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-deep-forest/10" data-testid={`admin-booking-row-${b.id}`}>
                  <td className="py-4">
                    <div className="font-medium text-deep-forest">{b.customer_name}</div>
                    <div className="text-xs text-deep-forest/50">{b.customer_email}</div>
                  </td>
                  <td className="py-4 text-deep-forest/80">{b.areas.length} · {b.total_area.toFixed(1)} m²</td>
                  <td className="py-4 text-deep-forest/80">{b.scheduled_date}</td>
                  <td className="py-4 text-deep-forest/80 max-w-[200px] truncate">{b.address}</td>
                  <td className="py-4 font-serif text-lg text-terracotta">${b.total_price.toFixed(2)}</td>
                  <td className="py-4 text-deep-forest/80">
                    <div className="flex items-center gap-2">
                      {b.payment_method === "cash" ? <Wallet className="w-3.5 h-3.5" /> : <CreditCard className="w-3.5 h-3.5" />}
                      <span className="capitalize">{b.payment_method || "card"}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs">{b.payment_status}</span>
                      {b.payment_method === "cash" && b.payment_status === "unpaid" && (
                        <button onClick={() => markPaid(b.id)} data-testid={`admin-mark-paid-${b.id}`} className="text-xs uppercase tracking-widest px-2 py-0.5 border border-terracotta text-terracotta hover:bg-terracotta hover:text-warm-sand transition-colors">
                          Mark paid
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`admin-status-btn-${b.id}`} className={`text-xs uppercase tracking-widest px-2 py-1 flex items-center gap-1 ${STATUS_CLS[b.status]}`}>
                          {b.status} <ChevronDown className="w-3 h-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {STATUSES.map((s) => (
                          <DropdownMenuItem key={s} data-testid={`admin-status-option-${b.id}-${s}`} onClick={() => changeStatus(b.id, s)} className="capitalize">{s.replace("_", " ")}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan="7" className="py-10 text-center text-deep-forest/60">No bookings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-deep-forest/60 border-b border-deep-forest/10">
                <th className="py-4">Name</th>
                <th className="py-4">Email</th>
                <th className="py-4">Provider</th>
                <th className="py-4">Role</th>
                <th className="py-4">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-deep-forest/10" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="py-4 font-medium text-deep-forest">{u.name}</td>
                  <td className="py-4 text-deep-forest/80">{u.email}</td>
                  <td className="py-4 capitalize text-deep-forest/80">{u.auth_provider}</td>
                  <td className="py-4">{u.is_admin ? <span className="text-xs uppercase tracking-widest px-2 py-1 bg-deep-forest text-warm-sand">admin</span> : <span className="text-deep-forest/60">customer</span>}</td>
                  <td className="py-4 text-deep-forest/60">{(u.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabBtn({ id, tab, setTab, label, testId, highlight }) {
  const active = tab === id;
  const highlightCls = highlight ? "text-terracotta font-semibold" : "";
  return (
    <button data-testid={testId} onClick={() => setTab(id)} className={`pb-4 -mb-px text-sm uppercase tracking-widest transition-colors ${active ? "text-terracotta border-b-2 border-terracotta" : `text-deep-forest/60 hover:text-deep-forest ${highlightCls}`}`}>
      {label}
    </button>
  );
}

function RefundsTable({ rows, onRetryRefund, onMarkRefunded, refundingId }) {
  if (rows.length === 0) {
    return (
      <div className="mt-10 py-16 text-center border border-dashed border-deep-forest/15" data-testid="admin-refunds-empty">
        <BadgeCheck className="w-6 h-6 mx-auto text-moss mb-3" />
        <p className="text-sm text-deep-forest/70">No refunds outstanding. Nicely done.</p>
      </div>
    );
  }
  return (
    <div className="mt-8 overflow-x-auto" data-testid="admin-refunds-table">
      <div className="mb-6 flex items-start gap-3 bg-status-pending-bg/60 border-l-2 border-terracotta px-4 py-3">
        <AlertTriangle className="w-4 h-4 text-terracotta mt-0.5" />
        <p className="text-xs text-deep-forest/80 leading-relaxed">
          Card cancellations refund automatically. Retry below if a refund failed. For cash bookings, hand back the money then mark as refunded.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-deep-forest/60 border-b border-deep-forest/10">
            <th className="py-4">Customer</th>
            <th className="py-4">Address</th>
            <th className="py-4">Amount</th>
            <th className="py-4">Method</th>
            <th className="py-4">Status</th>
            <th className="py-4">Cancelled</th>
            <th className="py-4 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} className="border-b border-deep-forest/10" data-testid={`admin-refund-row-${b.id}`}>
              <td className="py-4">
                <div className="font-medium text-deep-forest">{b.customer_name}</div>
                <div className="text-xs text-deep-forest/50">{b.customer_email}</div>
              </td>
              <td className="py-4 text-deep-forest/80 max-w-[240px] truncate">{b.address}</td>
              <td className="py-4 font-serif text-lg text-terracotta">${b.total_price.toFixed(2)}</td>
              <td className="py-4 text-deep-forest/80">
                <div className="flex items-center gap-2">
                  {b.payment_method === "cash" ? <Wallet className="w-3.5 h-3.5" /> : <CreditCard className="w-3.5 h-3.5" />}
                  <span className="capitalize">{b.payment_method}</span>
                </div>
              </td>
              <td className="py-4 text-xs">
                {b.refund_error ? (
                  <span className="text-terracotta" title={b.refund_error}>Failed · retry</span>
                ) : b.refund_status ? (
                  <span className="text-deep-forest/70 capitalize">{b.refund_status}</span>
                ) : (
                  <span className="text-deep-forest/70">Awaiting refund</span>
                )}
              </td>
              <td className="py-4 text-xs text-deep-forest/60">{(b.cancelled_at || "").slice(0, 10)}</td>
              <td className="py-4 text-right">
                {b.payment_method === "card" ? (
                  <button
                    onClick={() => onRetryRefund(b.id)}
                    disabled={refundingId === b.id}
                    data-testid={`admin-retry-refund-${b.id}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1.5 border border-terracotta text-terracotta hover:bg-terracotta hover:text-warm-sand transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-3 h-3 ${refundingId === b.id ? "animate-spin" : ""}`} />
                    Retry refund
                  </button>
                ) : (
                  <button
                    onClick={() => onMarkRefunded(b.id)}
                    disabled={refundingId === b.id}
                    data-testid={`admin-mark-refunded-${b.id}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1.5 border border-deep-forest text-deep-forest hover:bg-deep-forest hover:text-warm-sand transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Mark refunded
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AStat({ icon: Icon, label, value, testId, sub }) {
  return (
    <div className="bg-bone-white p-6" data-testid={testId}>
      <div className="flex items-center gap-2 label-overline"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-serif text-3xl text-deep-forest mt-3">{value}</div>
      {sub && <div className="text-xs text-terracotta/80 mt-2" data-testid={`${testId}-sub`}>{sub}</div>}
    </div>
  );
}
